import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import MDSpinner from "react-md-spinner";
import { toast, ToastContainer } from "react-toastify";
import {
  FaChartBar,
  FaMapMarkedAlt,
  FaTractor,
  FaUser,
} from "react-icons/fa";

import Title from "../../components/Title";
import Button from "../../components/Button";
import AgriDateTime from "../../components/AgriDateTime";
import ExportButton from "../../components/Export_button";
import { getTime } from "../../helpers/utils";
import SectionMapPicker from "./SectionMapPicker";
import {
  buildSectionWorkReportExportUrl,
  fetchSectionMapCatalog,
  fetchSectionReportMachines,
  fetchSectionReportOperators,
  fetchSectionWorkReport,
  fenceSourceLabel,
  groupRowsBySection,
  hasSectionSelection,
  summarizeSectionFilters,
  toggleAllSelectionIds,
  toggleSelectionId,
} from "./sectionReportApi";

import "./ReportBySection.css";

const FILTER_SECTIONS = "sections";
const FILTER_MACHINES = "machines";
const FILTER_OPERATORS = "operators";

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(date) {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 0);
  return copy;
}

function getFormatedDate(dt) {
  const data = new Date(dt);
  const dia = data.getDate().toString();
  const diaF = dia.length === 1 ? `0${dia}` : dia;
  const mes = (data.getMonth() + 1).toString();
  const mesF = mes.length === 1 ? `0${mes}` : mes;
  const anoF = data.getFullYear();
  const hour = (`0${data.getHours()}`).slice(-2).toString();
  const minutes = (`0${data.getMinutes()}`).slice(-2).toString();
  const seconds = (`0${data.getSeconds()}`).slice(-2).toString();
  return `${anoF}-${mesF}-${diaF}T${hour}:${minutes}:${seconds}`;
}

function applyTimeToDate(date, timeValue) {
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(`${date.toLocaleDateString("en-US", options)} ${timeValue}`);
}

function countSections(subFarmIds, landplotIds) {
  return subFarmIds.length + landplotIds.length;
}

function ReportBySection({ t }) {
  const { farm_id: farmId } = useParams();
  const [machines, setMachines] = useState([]);
  const [catalog, setCatalog] = useState({ sub_farms: [], landplots: [] });
  const [operators, setOperators] = useState([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState([]);
  const [selectedLandplotIds, setSelectedLandplotIds] = useState([]);
  const [selectedSubFarmIds, setSelectedSubFarmIds] = useState([]);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState([]);
  const [activeFilter, setActiveFilter] = useState(FILTER_SECTIONS);
  const [initialDate, setInitialDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [insideOnly, setInsideOnly] = useState(true);
  const [loadingBootstrap, setLoadingBootstrap] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);

  const groupedRows = useMemo(() => groupRowsBySection(rows), [rows]);

  const filterSummary = useMemo(
    () =>
      summarizeSectionFilters(
        catalog,
        selectedSubFarmIds,
        selectedLandplotIds
      ),
    [catalog, selectedSubFarmIds, selectedLandplotIds]
  );

  const sectionCount = countSections(selectedSubFarmIds, selectedLandplotIds);
  const machineIds = useMemo(
    () => machines.map((machine) => Number(machine.id)),
    [machines]
  );
  const operatorIds = useMemo(
    () => operators.map((operator) => Number(operator.id)),
    [operators]
  );

  const loadFilters = useCallback(async () => {
    setLoadingBootstrap(true);
    const [machinesResult, catalogResult, operatorsResult] =
      await Promise.allSettled([
        fetchSectionReportMachines(farmId),
        fetchSectionMapCatalog(farmId),
        fetchSectionReportOperators(farmId),
      ]);

    let hadError = false;

    if (machinesResult.status === "fulfilled") {
      setMachines(machinesResult.value);
    } else {
      hadError = true;
      setMachines([]);
      toast.error(t("Falha ao carregar máquinas."), {
        position: "bottom-right",
      });
    }

    if (catalogResult.status === "fulfilled") {
      setCatalog(catalogResult.value);
    } else {
      hadError = true;
      setCatalog({ sub_farms: [], landplots: [] });
      toast.error(t("Falha ao carregar seções do mapa."), {
        position: "bottom-right",
      });
    }

    if (operatorsResult.status === "fulfilled") {
      setOperators(operatorsResult.value);
    } else {
      hadError = true;
      setOperators([]);
      toast.error(t("Falha ao carregar operadores."), {
        position: "bottom-right",
      });
    }

    if (hadError) {
      console.error("ReportBySection filter load errors", {
        machines: machinesResult,
        catalog: catalogResult,
        operators: operatorsResult,
      });
    }

    setLoadingBootstrap(false);
  }, [farmId, t]);

  useEffect(() => {
    document.title = "Agriconnected - Relatórios";
    loadFilters();
  }, [loadFilters]);

  const handleGenerateReport = async () => {
    if (selectedMachineIds.length === 0) {
      toast.error(t("Selecione ao menos uma máquina."), {
        position: "bottom-right",
      });
      setActiveFilter(FILTER_MACHINES);
      return;
    }
    if (!hasSectionSelection(selectedSubFarmIds, selectedLandplotIds)) {
      toast.error(
        t("Selecione ao menos uma sub-fazenda ou talhão do mapa da fazenda."),
        { position: "bottom-right" }
      );
      setActiveFilter(FILTER_SECTIONS);
      return;
    }

    const dtIni = getFormatedDate(initialDate);
    const dtEnd = getFormatedDate(endDate);
    const query = {
      farmId,
      machineIds: selectedMachineIds,
      subFarmIds: selectedSubFarmIds,
      landplotIds: selectedLandplotIds,
      operatorIds: selectedOperatorIds,
      dtIni,
      dtEnd,
      insideOnly,
    };

    setLoadingReport(true);
    setSearched(true);
    setLastQuery(query);
    try {
      const data = await fetchSectionWorkReport(query);
      setRows(data);
    } catch (error) {
      toast.error(
        t("Falha ao gerar relatório. Recarregue a página e tente novamente."),
        { position: "bottom-right" }
      );
      setRows([]);
    } finally {
      setLoadingReport(false);
    }
  };

  const exportUrl =
    lastQuery && rows.length > 0
      ? buildSectionWorkReportExportUrl(lastQuery)
      : null;

  const renderMachineList = () => (
    <div className="report-section-panel">
      <div className="report-section-panel__head">
        <p>
          {t(
            "Escolha as máquinas que devem entrar no relatório da seção selecionada."
          )}
        </p>
        <button
          type="button"
          className="btn btn-link btn-sm report-section-link-btn"
          onClick={() =>
            setSelectedMachineIds(toggleAllSelectionIds(selectedMachineIds, machineIds))
          }
          disabled={!machines.length}
        >
          {selectedMachineIds.length === machines.length && machines.length > 0
            ? t("Limpar")
            : t("Selecionar todas")}
        </button>
      </div>
      <div className="report-section-check-list">
        {machines.length === 0 ? (
          <div className="report-section-check-empty">
            {t("Nenhuma máquina cadastrada nesta fazenda.")}
          </div>
        ) : (
          machines.map((machine) => {
            const checked = selectedMachineIds.includes(Number(machine.id));
            return (
              <label
                key={machine.id}
                className={`report-section-check-item ${checked ? "is-checked" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setSelectedMachineIds((current) =>
                      toggleSelectionId(current, machine.id)
                    )
                  }
                />
                <span>{machine.ident}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  const renderOperatorList = () => (
    <div className="report-section-panel">
      <div className="report-section-panel__head">
        <p>
          {t(
            "Opcional: filtre por operadores vinculados às operações no período."
          )}
        </p>
        <button
          type="button"
          className="btn btn-link btn-sm report-section-link-btn"
          onClick={() =>
            setSelectedOperatorIds(
              toggleAllSelectionIds(selectedOperatorIds, operatorIds)
            )
          }
          disabled={!operators.length}
        >
          {selectedOperatorIds.length === operators.length && operators.length > 0
            ? t("Limpar")
            : t("Selecionar todos")}
        </button>
      </div>
      <div className="report-section-check-list">
        {operators.length === 0 ? (
          <div className="report-section-check-empty">
            {t("Nenhum operador cadastrado nesta fazenda.")}{" "}
            <Link to={`/${farmId}/operator/add`}>{t("Cadastrar operador")}</Link>
          </div>
        ) : (
          operators.map((operator) => {
            const checked = selectedOperatorIds.includes(Number(operator.id));
            const label = operator.nome || operator.ident;
            return (
              <label
                key={operator.id}
                className={`report-section-check-item ${checked ? "is-checked" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setSelectedOperatorIds((current) =>
                      toggleSelectionId(current, operator.id)
                    )
                  }
                />
                <span>
                  {label}
                  {operator.ident ? <small> ({operator.ident})</small> : null}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div id="report_section_container">
      <ToastContainer />
      {exportUrl && (
        <ExportButton
          classname="btn btn-default-export btn-export-extra"
          export={exportUrl}
          title={t("Exportar planilha")}
        />
      )}

      <Title size="h1" farm_name={t("Relatório por seção")} />

      <div className="report-section-card">
        <h2 className="report-section-card__title">
          <FaChartBar aria-hidden="true" />
          {t("Gerar relatório por seção")}
        </h2>
        <p className="report-section-hint">
          {t(
            "Selecione seções do mapa, máquinas e operadores. Depois escolha o período e gere o relatório."
          )}{" "}
          <Link to={`/${farmId}/farm-map/`}>{t("Abrir mapa da fazenda")}</Link>
        </p>

        <div
          className="report-section-filter-bar"
          role="tablist"
          aria-label={t("Filtros do relatório por seção")}
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeFilter === FILTER_SECTIONS}
            className={`report-section-filter-btn ${
              activeFilter === FILTER_SECTIONS ? "is-active" : ""
            }`}
            onClick={() => setActiveFilter(FILTER_SECTIONS)}
          >
            <FaMapMarkedAlt aria-hidden="true" />
            <span>{t("Seções")}</span>
            {sectionCount > 0 ? (
              <span className="report-section-filter-badge">{sectionCount}</span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeFilter === FILTER_MACHINES}
            className={`report-section-filter-btn ${
              activeFilter === FILTER_MACHINES ? "is-active" : ""
            }`}
            onClick={() => setActiveFilter(FILTER_MACHINES)}
          >
            <FaTractor aria-hidden="true" />
            <span>{t("Máquinas")}</span>
            {selectedMachineIds.length > 0 ? (
              <span className="report-section-filter-badge">
                {selectedMachineIds.length}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeFilter === FILTER_OPERATORS}
            className={`report-section-filter-btn ${
              activeFilter === FILTER_OPERATORS ? "is-active" : ""
            }`}
            onClick={() => setActiveFilter(FILTER_OPERATORS)}
          >
            <FaUser aria-hidden="true" />
            <span>{t("Operadores")}</span>
            {selectedOperatorIds.length > 0 ? (
              <span className="report-section-filter-badge">
                {selectedOperatorIds.length}
              </span>
            ) : null}
          </button>
        </div>

        {loadingBootstrap ? (
          <div className="report-section-spinner">
            <MDSpinner />
          </div>
        ) : (
          <div className="report-section-filter-panel">
            {activeFilter === FILTER_SECTIONS && (
              <SectionMapPicker
                catalog={catalog}
                selectedSubFarmIds={selectedSubFarmIds}
                selectedLandplotIds={selectedLandplotIds}
                onSubFarmChange={setSelectedSubFarmIds}
                onLandplotChange={setSelectedLandplotIds}
              />
            )}
            {activeFilter === FILTER_MACHINES && renderMachineList()}
            {activeFilter === FILTER_OPERATORS && renderOperatorList()}
          </div>
        )}

        <div className="report-section-period-row">
          <div className="report-section-check">
            <input
              type="checkbox"
              id="section_inside_only"
              checked={insideOnly}
              onChange={(e) => setInsideOnly(e.target.checked)}
              className="check"
            />
            <label className="form-check-label" htmlFor="section_inside_only">
              {t("Somente dentro do perímetro")}
            </label>
          </div>
          <div className="report-section-date-field">
            <AgriDateTime
              time={getTime(initialDate)}
              label={t("Data inicial")}
              datetimeformat="true"
              selectedDate={initialDate}
              handleDateChange={setInitialDate}
              handleTimeChange={(e) =>
                setInitialDate(applyTimeToDate(initialDate, e.target.value))
              }
            />
          </div>
          <div className="report-section-date-field">
            <AgriDateTime
              time={getTime(endDate)}
              label={t("Data final")}
              datetimeformat="true"
              selectedDate={endDate}
              handleDateChange={setEndDate}
              handleTimeChange={(e) =>
                setEndDate(applyTimeToDate(endDate, e.target.value))
              }
            />
          </div>
          <div className="report-section-generate-wrap">
            <Button click={handleGenerateReport} class="btn-primary">
              {t("Gerar Relatório")}
            </Button>
          </div>
        </div>

        {hasSectionSelection(selectedSubFarmIds, selectedLandplotIds) && (
          <p className="report-section-summary">
            {filterSummary.subFarmCount > 0 && (
              <>
                {t("Sub-fazendas")}: {filterSummary.subFarmCount}
                {filterSummary.landplotCount > 0 ? " · " : ""}
              </>
            )}
            {filterSummary.landplotCount > 0 && (
              <>
                {t("Talhões")}: {filterSummary.landplotCount}
              </>
            )}
            {selectedMachineIds.length > 0 && (
              <>
                {" · "}
                {t("Máquinas")}: {selectedMachineIds.length}
              </>
            )}
            {selectedOperatorIds.length > 0 && (
              <>
                {" · "}
                {t("Operadores")}: {selectedOperatorIds.length}
              </>
            )}
          </p>
        )}
      </div>

      {!loadingBootstrap && loadingReport && (
        <div className="report-section-spinner">
          <MDSpinner />
        </div>
      )}

      {!loadingReport && searched && rows.length === 0 && (
        <div className="x_panel_no_width col-xs-12">
          <h2 className="x_title">{t("Nenhum resultado encontrado")}</h2>
          <p className="report-section-hint">
            {t(
              "Nenhuma posição GPS foi encontrada no perímetro selecionado para o intervalo informado."
            )}
          </p>
        </div>
      )}

      {!loadingReport &&
        groupedRows.map(({ sectionName, rows: sectionRows }) => (
          <div key={sectionName} className="report-section-block">
            <div className="x_title title">
              <h2>{sectionName}</h2>
              <div className="clearfix" />
            </div>
            <div className="table-responsive">
              <table className="table table-striped jambo_table report-section-table">
                <thead>
                  <tr>
                    <th>{t("Talhão")}</th>
                    <th>{t("Cerca")}</th>
                    <th>{t("Máquina")}</th>
                    <th>{t("Operador")}</th>
                    <th>{t("Início")}</th>
                    <th>{t("Fim")}</th>
                    <th>{t("Horas trabalho")}</th>
                    <th>{t("Horas ocioso")}</th>
                    <th>{t("Horas total")}</th>
                    <th>{t("Vel. média")}</th>
                    <th>{t("Vel. máx.")}</th>
                    <th>{t("Km")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionRows.map((row) => (
                    <tr
                      key={`${row.section_farm_id}-${row.land_plot_id}-${row.equipment_id}-${row.operator_id}-${row.date_time_ini}`}
                    >
                      <td>{row.land_plot}</td>
                      <td>
                        <span className="report-section-tag">
                          {fenceSourceLabel(
                            t,
                            row.fence_source,
                            row.hole_count
                          )}
                        </span>
                      </td>
                      <td>{row.equipment}</td>
                      <td>{row.operator}</td>
                      <td>{row.date_time_ini}</td>
                      <td>{row.date_time_end}</td>
                      <td>{row.hours_work}</td>
                      <td>{row.hours_idle}</td>
                      <td>{row.hours_totaly}</td>
                      <td>{row.speed_avg}</td>
                      <td>{row.speed_max}</td>
                      <td>{row.km_rolled}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
}

export default withTranslation()(ReportBySection);
